import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export function FeaturedProjectsSlider({ projects }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // Auto-advance slider every 6 seconds
  useEffect(() => {
    if (projects.length <= 1) return;
    
    const timer = setInterval(() => {
      nextSlide();
    }, 6000);
    
    return () => clearInterval(timer);
  }, [currentIndex, projects.length]);

  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % projects.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
  };

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  if (!projects || projects.length === 0) return null;

  const currentProject = projects[currentIndex];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <section className="mb-16 sm:mb-20" data-testid="featured-projects-slider">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Label */}
        <div className="mb-6">
          <p className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] uppercase tracking-wider">
            Featured Projects
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative">
          {/* Main Slide */}
          <div className="relative aspect-[21/9] rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))]">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="absolute inset-0"
              >
                <Link 
                  to={`/work/${currentProject.slug}`}
                  className="block w-full h-full group"
                  data-testid={`featured-slide-${currentProject.slug}`}
                >
                  {/* Background Image */}
                  <img
                    src={currentProject.thumbnail || currentProject.poster}
                    alt={currentProject.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex items-end p-8 sm:p-12 lg:p-16">
                    <div className="max-w-[600px] space-y-4">
                      <p className="font-mono-meta text-xs sm:text-sm text-white/80 uppercase tracking-wider">
                        {currentProject.category} · {currentProject.year}
                      </p>
                      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
                        {currentProject.title}
                      </h2>
                      {currentProject.logline && (
                        <p className="font-body text-sm sm:text-base text-white/90 leading-relaxed line-clamp-2">
                          {currentProject.logline}
                        </p>
                      )}
                      
                      {/* Play Button Indicator */}
                      <div className="flex items-center gap-3 pt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                          <Play className="w-5 h-5 text-white fill-white ml-1" />
                        </div>
                        <span className="font-mono-meta text-xs text-white uppercase tracking-wider">
                          View Project
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows (only show if more than 1 project) */}
            {projects.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-black/60 transition-all z-10"
                  aria-label="Previous slide"
                  data-testid="featured-slider-prev"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-black/60 transition-all z-10"
                  aria-label="Next slide"
                  data-testid="featured-slider-next"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
          </div>

          {/* Dots Navigation (only show if more than 1 project) */}
          {projects.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {projects.map((project, index) => (
                <button
                  key={project.id || project.slug}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex
                      ? 'w-8 h-2 bg-[hsl(var(--bk-accent))]'
                      : 'w-2 h-2 bg-[hsl(var(--bk-fg-subtle))] hover:bg-[hsl(var(--bk-fg-muted))]'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  data-testid={`featured-slider-dot-${index}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
